import React, { useRef, useState, useEffect, useMemo } from "react";
import { Shield, Camera, Lock, Grid, List, Search, X } from "lucide-react"; // Added Grid, List, Search, X
import PhotoModal from "../components/Gallery/PhotoModal";
import PhotoList from "../components/Gallery/PhotoList";
import { useQueryParam } from "../utils/getQueryParam";
import { getEventIdFromPath } from "../utils/getEventId";
import { fetchVerifyPassKey } from "../utils/fetchData";
import { BASE_URL } from "../utils/enum";
import LazyImageCard from "../components/Gallery/LazyImageCard";

// ----------------------------------------------------------------------
// 1. New/Refactored Components for better Structure and UI/UX
// ----------------------------------------------------------------------

/**
 * Custom Header Component for a fixed top bar
 */
const Header = ({ title, rightContent, onClear }) => (
  <header className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 shadow-lg z-30 p-4 flex items-center justify-between">
    <div className="flex items-center space-x-3">
      <div className="p-2 bg-blue-600 rounded-lg">
        <Camera className="h-6 w-6 text-white" />
      </div>
      <span className="text-xl font-bold text-gray-900">PhotoLogy</span>
    </div>
    <div className="flex items-center space-x-4">
      {rightContent}
      {onClear && (
        <button
          onClick={onClear}
          className="text-sm bg-red-500 text-white px-3 py-1 rounded-full flex items-center hover:bg-red-600 transition-colors">
          <X className="w-4 h-4 mr-1" /> Clear
        </button>
      )}
    </div>
  </header>
);

/**
 * Full View Cover Page Component
 */
const CoverImage = ({ image, onClick, title }) => (
  <div
    className="relative w-full h-[70vh] cursor-pointer group shadow-xl"
    onClick={onClick}>
    <img
      src={image.preview || image.thumbnail || image.url}
      alt="Cover"
      className="w-full h-full object-cover object-center"
    />
    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-end p-6">
      <h2 className="text-3xl font-extrabold text-white">{title}</h2>
    </div>
    <div className="absolute top-4 right-4 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
      View Full Image
    </div>
  </div>
);

// ----------------------------------------------------------------------
// 2. Main FaceVerification Component (Functional Code Intact)
// ----------------------------------------------------------------------

export default function FaceVerification() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // 🔐 PIN state
  const [pin, setPin] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [pinError, setPinError] = useState("");

  // Existing states
  const [formData, setFormData] = useState({ name: "", email: "", phone: "" });
  const [capturedImage, setCapturedImage] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | verifying | success | failed
  const [resultImages, setResultImages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [view, setView] = useState("grid"); // "grid" | "list"
  const [currentIndex, setCurrentIndex] = useState(0);
  const [eventDetail, setEventDetail] = useState(null);
  const [queryPin, setQueryPin] = useState(useQueryParam("pin") || "");
  const [highlightImages, setHighlightImages] = useState([]);
  const [activeTab, setActiveTab] = useState("Verified Matches");
  const eventId = getEventIdFromPath(location.pathname);

  // The original correctPin is not used in the final submission check, but kept here for clarity.
  // const correctPin = "1234"; // ✅ change this to your real PIN

  // --- Utility Functions/Hooks ---

  const clearVerification = () => {
    localStorage.removeItem("lastVerification");
    setFormData({ name: "", email: "", phone: "" });
    setCapturedImage(null);
    setResultImages([]);
    setHighlightImages([]);
    setPreviewImage(null);
    setCurrentIndex(0);
    setSearchQuery("");
    setActiveTab("Verified Matches");
    setStatus("idle");
  };

  const currentImages =
    activeTab === "Verified Matches" ? resultImages : highlightImages;

  const filteredImages = useMemo(() => {
    return currentImages?.filter(
      (img) =>
        // Filter based on the image URL or any other searchable property
        img?.url?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        img?.thumbnail?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [currentImages, searchQuery]);

  // NOTE: filteredResults was defined in the original code but only used for modal navigation.
  // We'll update modal navigation to use filteredImages based on the active tab for a better experience.
  const filteredResults = resultImages?.filter((img) =>
    img?.url?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const showNextImage = () => {
    const listToNavigate = filteredImages;
    const currentIndexInList = listToNavigate.findIndex(
      (p) => p.thumbnail === previewImage.thumbnail
    );

    if (currentIndexInList < listToNavigate.length - 1) {
      setPreviewImage(listToNavigate[currentIndexInList + 1]);
    }
  };

  const showPrevImage = () => {
    const listToNavigate = filteredImages;
    const currentIndexInList = listToNavigate.findIndex(
      (p) => p.thumbnail === previewImage.thumbnail
    );

    if (currentIndexInList > 0) {
      setPreviewImage(listToNavigate[currentIndexInList - 1]);
    }
  };

  const verifyAndFetch = async () => {
    try {
      if ((pin || queryPin) && eventId && !authenticated) {
        const result = await fetchVerifyPassKey(eventId, pin || queryPin);
        if (result.success) {
          setAuthenticated(true);
          setPinError("");
        } else {
          setPinError("Invalid PIN");
          return; // stop if PIN is wrong
        }
      }

      if (eventId && authenticated) {
        const res = await fetch(`${BASE_URL}/api/events/${eventId}`);

        if (!res.ok) throw new Error("Event not found");

        const data = await res.json();
        setEventDetail(data);
      }
    } catch (err) {
      console.error("Error verifying or fetching event:", err);
      setPinError("Something went wrong, please try again");
    }
  };

  useEffect(() => {
    const fetchEventsRequest = async () => {
      if (eventId && authenticated) {
        const res = await fetch(`${BASE_URL}/api/events/${eventId}`);

        if (!res.ok) throw new Error("Event not found");

        const data = await res.json();
        setEventDetail(data);
      }
    };
    fetchEventsRequest();
  }, [eventId, authenticated]);

  // PIN submission
  const handlePinSubmit = async (e) => {
    e.preventDefault();
    await verifyAndFetch();
  };

  useEffect(() => {
    if (isStreaming && videoRef.current) {
      navigator.mediaDevices
        .getUserMedia({ video: true })
        .then((stream) => {
          videoRef.current.srcObject = stream;
        })
        .catch((err) => {
          console.error("Error accessing camera:", err);
        });
    }
  }, [isStreaming]);

  const startCamera = () => setIsStreaming(true);

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      stream.getTracks().forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
    setIsStreaming(false);
  };

  const capturePhoto = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const context = canvas.getContext("2d");

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = canvas.toDataURL("image/png");
    setCapturedImage(imageData);
    stopCamera();
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    setIsStreaming(true);
  };

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!capturedImage) {
      alert("Please take a selfie first.");
      return;
    }

    try {
      setStatus("verifying");

      // Convert base64 to Blob
      const byteString = atob(capturedImage.split(",")[1]);
      const mimeString = capturedImage
        .split(",")[0]
        .split(":")[1]
        .split(";")[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++)
        ia[i] = byteString.charCodeAt(i);
      const blob = new Blob([ab], { type: mimeString });

      // Prepare form data
      const formDataPayload = new FormData();
      formDataPayload.append("file", blob, "selfie.png");
      formDataPayload.append(
        "folder_key",
        eventDetail?.path.split("/").join("")
      );

      // Face verification API
      const response = await fetch("https://site.platformown.com/match-face", {
        method: "POST",
        body: formDataPayload,
      });

      if (!response.ok) throw new Error("Server error");

      const result = await response.json();
      let highlightData = { images: [] };

      // ✅ Fetch highlights (independent of match success)
      try {
        const highlightResponse = await fetch(
          `${BASE_URL}/api/images/${eventDetail?.id}/category/highlight`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (highlightResponse.ok) {
          highlightData = await highlightResponse.json();
          setHighlightImages(highlightData.images || []);
        } else {
          console.warn("No highlight images found");
        }
      } catch (highlightError) {
        console.error("Failed to fetch highlight images:", highlightError);
      }

      // ✅ Process verification result
      if (result?.matched_images?.length > 0) {
        setResultImages(result.matched_images);
        setStatus("success");
      } else {
        setStatus("failed");
      }

      // ✅ Save everything in localStorage
      const saveData = {
        capturedImage,
        matchedImages: result?.matched_images || [],
        highlightImages: highlightData?.images || [],
        eventId: eventDetail?.id,
        folderKey: eventDetail?.path,
        timestamp: Date.now(),
      };

      localStorage.setItem("lastVerification", JSON.stringify(saveData));

      console.log("Saved verification data:", saveData);
    } catch (error) {
      console.error("Error during verification:", error);
      setStatus("failed");
    }
  };

  useEffect(() => {
    const saved = localStorage.getItem("lastVerification");
    if (saved) {
      const parsed = JSON.parse(saved);
      // NOTE: Restoring formData is complex since you don't use it in success state,
      // focusing on image data restoration for the gallery view.
      setCapturedImage(parsed.capturedImage);
      setResultImages(parsed.matchedImages);
      setHighlightImages(parsed.highlightImages || []);
      // Only set status to success if there are images to show
      if (
        parsed.matchedImages?.length > 0 ||
        parsed.highlightImages?.length > 0
      ) {
        setStatus("success");
      } else {
        setStatus("idle");
      }
    }
    verifyAndFetch();
  }, []);

  // 🔒 Show PIN lock first
  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-2xl shadow-xl w-80 text-center">
          <Lock className="w-12 h-12 mx-auto text-blue-600 mb-3" />
          <h2 className="text-2xl font-bold mb-4">Enter PIN</h2>
          <form onSubmit={handlePinSubmit} className="space-y-4">
            <input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Enter PIN"
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            />
            {pinError && <p className="text-red-500 text-sm">{pinError}</p>}
            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition">
              Unlock
            </button>
          </form>
        </div>
      </div>
    );
  }

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setCapturedImage(reader.result); // set the uploaded image
      setIsStreaming(false); // stop camera if open
    };
    reader.readAsDataURL(file);
  };

  // ✅ Verification Success: Gallery View
  if (status === "success") {
    // Determine the main cover image
    const coverImage = resultImages[0] || highlightImages[0];
    const galleryTitle = eventDetail?.name || "Image Gallery";

    // Determine the list used for modal navigation based on the active tab
    const modalImageList = filteredImages;

    return (
      <div className="min-h-screen bg-white">
        {/* Fixed Header: Full view cover page on top with full view along with header */}
        <Header
          title={galleryTitle}
          onClear={clearVerification}
          rightContent={
            <div className="flex gap-2">
              <button
                onClick={() => setView("grid")}
                className={`p-2 rounded-full transition-colors ${
                  view === "grid"
                    ? "bg-blue-600 text-white shadow-md"
                    : "text-gray-500 hover:bg-gray-100"
                }`}
                title="Grid View">
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setView("list")}
                className={`p-2 rounded-full transition-colors ${
                  view === "list"
                    ? "bg-blue-600 text-white shadow-md"
                    : "text-gray-500 hover:bg-gray-100"
                }`}
                title="List View">
                <List className="w-5 h-5" />
              </button>
            </div>
          }
        />

        {/* Main Content Area, padded to clear the fixed header */}
        <main className="relative">
          {/* Cover Page */}
          {coverImage && (
            <CoverImage
              image={coverImage}
              title={
                eventDetail?.name
              }
              onClick={() => {
                setPreviewImage(coverImage);
                // The current index logic is handled implicitly by the modal
              }}
            />
          )}

          {/* Gallery Section */}
          <div className="p-4 sm:p-8">
            <h2 className="text-3xl font-bold mb-6 text-gray-800">
              {activeTab} 🖼️
            </h2>

            {/* Toolbar (Search and Tabs) - Full UI UX fix */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 mb-6">
              {/* Search Bar */}
              <div className="relative w-full sm:w-1/3">
                <input
                  type="text"
                  placeholder={`Search ${activeTab.toLowerCase()}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full border border-gray-300 rounded-xl pl-10 pr-4 py-3 text-md focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow shadow-sm"
                />
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              </div>

              {/* Tabs Header */}
              <div className="flex border-2 border-gray-200 rounded-xl p-1 bg-gray-100">
                {["Verified Matches", "Highlights"].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => {
                      setActiveTab(tab);
                      setSearchQuery(""); // Clear search on tab switch for new context
                    }}
                    className={`px-6 py-2 text-md font-semibold rounded-lg transition-colors min-w-[150px] ${
                      activeTab === tab
                        ? "bg-white text-blue-600 shadow-md"
                        : "text-gray-500 hover:text-blue-500 hover:bg-white/50"
                    }`}>
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            {/* Images Grid/List: grid with respect to image and section all or hignlight and show type */}
            {filteredImages.length > 0 ? (
              view === "grid" ? (
                <div
                  key={activeTab + "-grid"}
                  className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
                  {filteredImages.map((img, idx) => (
                    <LazyImageCard
                      key={idx}
                      img={img}
                      onClick={() => setPreviewImage(img)}
                    />
                  ))}
                </div>
              ) : (
                <PhotoList
                  photos={filteredImages}
                  onPhotoClick={setPreviewImage}
                />
              )
            ) : (
              <p className="text-center text-xl text-gray-500 py-10">
                {searchQuery
                  ? `No results found for "${searchQuery}" in ${activeTab}.`
                  : `No ${activeTab.toLowerCase()} available.`}
              </p>
            )}
          </div>
        </main>

        {/* Photo Modal for face preview */}
        {previewImage !== null && (
          <>
            {/* NOTE: Moved the clear verification button to the fixed Header for better UX. */}
            <PhotoModal
              photo={previewImage}
              photos={modalImageList} // Use the correct list for modal context
              currentIndex={modalImageList.findIndex(
                (p) => p.thumbnail === previewImage.thumbnail
              )}
              onClose={() => setPreviewImage(null)}
              onPrev={showPrevImage}
              onNext={showNextImage}
            />
          </>
        )}
      </div>
    );
  }

  // ❌ Verification Failed
  if (status === "failed") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-2xl shadow-xl w-80 text-center">
          <h3 className="text-3xl font-bold text-red-600 mb-4">
            ❌ No Match Found
          </h3>
          <p className="text-lg text-gray-700 mb-6">
            We couldn't find a match for your selfie. Please try again.
          </p>
          <button
            onClick={retakePhoto}
            className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition">
            Retake Selfie
          </button>
          <button
            onClick={() => setStatus("idle")}
            className="w-full mt-3 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition">
            Go Back to Form
          </button>
        </div>
      </div>
    );
  }

  // 🔄 Default flow (idle | verifying)
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-lg relative">
        <div className="text-center mb-6">
          <Shield className="h-12 w-12 text-blue-600 mx-auto mb-2" />
          <h1 className="text-2xl font-bold">Selfie Verification</h1>
          <p className="text-gray-500">
            Fill the form and verify with a selfie
          </p>
        </div>

        {/* Camera or Preview */}
        <div className="mb-6 relative w-full aspect-video rounded-lg overflow-hidden bg-black">
          {!capturedImage && isStreaming && (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="absolute inset-0 w-full h-full object-cover transform scale-x-[-1]"
              />
              {/* Head shape overlay */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <svg
                  viewBox="0 0 200 250"
                  className="w-40 h-52 opacity-70"
                  fill="none"
                  stroke="white"
                  strokeWidth="3">
                  <path d="M100 20 C 150 20, 170 70, 170 110 C 170 160, 140 200, 100 200 C 60 200, 30 160, 30 110 C 30 70, 50 20, 100 20 Z" />
                </svg>
              </div>
            </>
          )}

          {!capturedImage && !isStreaming && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100">
              <Camera className="h-12 w-12 text-gray-400 mb-2" />
              <button
                onClick={startCamera}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Start Camera
              </button>
            </div>
          )}

          {capturedImage && (
            <img
              src={capturedImage}
              alt="Captured"
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
        </div>

        {/* Capture / Retake Buttons */}
        <div className="flex justify-center gap-4 mb-6">
          {isStreaming && !capturedImage && (
            <button
              onClick={capturePhoto}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700">
              Capture Selfie
            </button>
          )}
          {/* Upload button */}
          {!isStreaming && !capturedImage && (
            <label className="bg-purple-600 text-white px-4 py-2 rounded-lg cursor-pointer hover:bg-purple-700">
              📁 Upload Photo
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleImageUpload(e)}
              />
            </label>
          )}
          {capturedImage && (
            <button
              onClick={retakePhoto}
              className="bg-gray-300 px-4 py-2 rounded-lg hover:bg-gray-400">
              Retake
            </button>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            name="name"
            placeholder="Full Name"
            value={formData.name}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            required
          />
          <input
            type="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            required
          />
          <input
            type="tel"
            name="phone"
            placeholder="Phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            required
          />
          <button
            type="submit"
            disabled={status === "verifying" || !capturedImage}
            className={`w-full text-white py-2 rounded-lg transition ${
              status === "verifying"
                ? "bg-blue-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}>
            {status === "verifying" ? "⏳ Verifying..." : "Submit"}
          </button>
        </form>

        {/* Status + Results */}
        {status === "verifying" && (
          <p className="text-center text-gray-600 mt-4">
            ⏳ Verifying your face...
          </p>
        )}
      </div>

      {/* Hidden Canvas */}
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
}
